import AppLayout from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  FileCheck, CheckCircle2, Download, FileText, Clock, AlertTriangle, Calendar,
  Info, ArrowRight, Users
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

const closeoutData = {
  project: "77 Clayton Rd Units",
  address: "77 Clayton Rd, Clayton VIC",
  council: "Monash",
  permitNumber: "MCC-2024-0198",
  totalConditions: 20,
  acceptedEvidence: 20,
  outstandingClarifications: 0,
  completionDate: "28/12/2024",
  stages: [
    { name: "Pre-Start", conditions: 4, cleared: 4 },
    { name: "Pre-Slab", conditions: 5, cleared: 5 },
    { name: "Pre-Frame", conditions: 3, cleared: 3 },
    { name: "Pre-OC", conditions: 6, cleared: 6 },
    { name: "Ongoing", conditions: 2, cleared: 2 },
  ],
  evidenceIndex: [
    { num: "1.1", title: "Construction Management Plan", file: "CMP_77Clayton_Final.pdf", acceptedDate: "15/08/2024" },
    { num: "1.2", title: "Sediment Controls", file: "Sediment_Photos_77Clayton.zip", acceptedDate: "18/08/2024" },
    { num: "2.1", title: "Drainage Certificate", file: "Drainage_Cert_77Clayton.pdf", acceptedDate: "10/09/2024" },
    { num: "2.2", title: "Soil Report", file: "Soil_Report_77Clayton.pdf", acceptedDate: "12/09/2024" },
    { num: "3.1", title: "Acoustic Report", file: "Acoustic_Report_77Clayton.pdf", acceptedDate: "01/10/2024" },
    { num: "4.1", title: "Landscape Completion", file: "Landscape_Photos_77Clayton.zip", acceptedDate: "20/12/2024" },
    { num: "4.2", title: "Car Park Line Marking", file: "CarPark_Photos_77Clayton.zip", acceptedDate: "22/12/2024" },
  ],
  timeline: [
    { date: "01/08/2024", event: "Pre-Start gate cleared" },
    { date: "05/09/2024", event: "Pre-Slab gate cleared" },
    { date: "10/10/2024", event: "Pre-Frame gate cleared" },
    { date: "25/12/2024", event: "Pre-OC gate cleared" },
    { date: "28/12/2024", event: "Final closeout complete" },
  ],
};

export default function CloseoutView() {
  return (
    <AppLayout>
      <div className="space-y-5" data-testid="closeout-view-page">
        {/* Page explainers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Final pack summary and evidence index for completed milestones. Full audit trail.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <ArrowRight className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Download the completed pack, export evidence index, or generate a compliance summary.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Users className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Who uses this</p>
                <p className="text-xs text-slate-500">Building surveyor. Final review and closeout confirmation.</p>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Project summary */}
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardContent className="py-5 px-6 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-slate-900">{closeoutData.project}</h3>
                <p className="text-sm text-slate-500">{closeoutData.address} - {closeoutData.council} Council</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status="complete" />
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">All conditions cleared</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Summary stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="py-4 px-4 text-center">
              <p className="text-2xl font-heading font-bold text-emerald-600">{closeoutData.totalConditions}</p>
              <p className="text-xs text-slate-500">Total conditions</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="py-4 px-4 text-center">
              <p className="text-2xl font-heading font-bold text-emerald-600">{closeoutData.acceptedEvidence}</p>
              <p className="text-xs text-slate-500">Accepted evidence</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="py-4 px-4 text-center">
              <p className="text-2xl font-heading font-bold text-slate-400">{closeoutData.outstandingClarifications}</p>
              <p className="text-xs text-slate-500">Outstanding items</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="py-4 px-4 text-center">
              <p className="text-lg font-heading font-bold text-slate-900">{closeoutData.completionDate}</p>
              <p className="text-xs text-slate-500">Completion date</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Stage breakdown */}
          <Card className="border-slate-200" data-testid="stage-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading">Stage Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {closeoutData.stages.map((s) => (
                <div key={s.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-slate-700">{s.name}</span>
                  </div>
                  <span className="text-xs text-slate-400">{s.cleared}/{s.conditions}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Evidence index */}
          <Card className="lg:col-span-2 border-slate-200" data-testid="evidence-index">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading">Evidence Index</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {closeoutData.evidenceIndex.map((ev) => (
                  <div key={ev.num} className="flex items-center gap-3 bg-slate-50 rounded-lg p-2.5">
                    <span className="font-mono text-[10px] text-slate-400 w-8 shrink-0">{ev.num}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900">{ev.title}</p>
                      <p className="text-[10px] text-slate-400">{ev.file}</p>
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0">{ev.acceptedDate}</span>
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Timeline */}
        <Card className="border-slate-200" data-testid="review-timeline">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              Review Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-0 overflow-x-auto pb-2">
              {closeoutData.timeline.map((t, i) => (
                <div key={i} className="flex items-center">
                  <div className="text-center min-w-[140px]">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-1">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <p className="text-xs font-medium text-slate-900">{t.event}</p>
                    <p className="text-[10px] text-slate-400">{t.date}</p>
                  </div>
                  {i < closeoutData.timeline.length - 1 && (
                    <div className="w-12 h-0.5 bg-emerald-200 shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Download */}
        <div className="flex gap-3">
          <Button className="bg-slate-900 text-white hover:bg-slate-800 gap-2" data-testid="download-closeout-btn">
            <Download className="w-4 h-4" /> Download closeout pack
          </Button>
          <Button variant="outline" className="gap-2">
            <FileText className="w-4 h-4" /> View summary PDF
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
